import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { base, unwrap, withParty } from '@/app/cancellations/engine/api-shapes';
import { type ICancellationImpact } from '@/app/cancellations/types';
import { useStrongHoldEstimate } from '@/hooks/queries/strong-hold/use-strong-hold';
import { coreApi } from '@/lib/api-client';

import { cancellationKeys } from './keys';
import { retryCancellation } from './use-cancellations';

/** Register + submitted results for one cancelled unit. Off unless `capabilities.impact`. */
export function useCancellationImpact(args: {
	electionId: number;
	partyId: number | null;
	partyKey: number;
	id: number;
	enabled: boolean;
}) {
	const { electionId, partyId, partyKey, id, enabled } = args;
	return useQuery({
		queryKey: cancellationKeys.impact(electionId, partyKey, id),
		queryFn: async ({ signal }): Promise<ICancellationImpact> => {
			const { data } = await coreApi.get(withParty(`${base(electionId)}/${id}/impact`, partyId), { signal });
			return unwrap<ICancellationImpact>(data);
		},
		enabled: enabled && electionId > 0 && id > 0,
		staleTime: 30_000,
		retry: retryCancellation,
	});
}

export interface IUnitEstimate {
	code: string;
	registeredVoters: number | null;
	expectedVotes: number | null;
}

/**
 * Per-unit expected votes from the Strong Hold estimate (api.query). The cancellation impact
 * route deliberately does not carry them — they are a plan, not a fact, and every surface
 * that shows one labels it "estimate".
 *
 * Same key the Strong Hold page uses (saved plan, no actuals, `Ward` grain), so an open
 * Strong Hold tab and the alert stack share one request. Returns `null` while unknown — an
 * absent estimate route must render "—", not "0 expected votes".
 */
export function useUnitEstimates(
	electionId: number,
	partyId: number | null,
	enabled: boolean
): Map<string, IUnitEstimate> | null {
	const { data } = useStrongHoldEstimate({ electionId, partyId, includeActuals: false, grain: 'Ward', enabled });
	const units = data?.units;
	return useMemo(() => {
		if (!units) return null;
		const map = new Map<string, IUnitEstimate>();
		for (const u of units) {
			map.set(u.code, { code: u.code, registeredVoters: u.registeredVoters, expectedVotes: u.expectedVotes });
		}
		return map;
	}, [units]);
}

/** `undefined` = no estimate at all (render "—"); `null` = estimate has no figure for this unit. */
export const expectedFor = (estimates: Map<string, IUnitEstimate> | null, code: string): number | null | undefined => {
	if (!estimates) return undefined;
	const unit = estimates.get(code);
	return unit ? unit.expectedVotes : null;
};
